import React, { useState } from 'react';
import type { AdminSessionItem } from '../../lib/types';

interface SessionsSectionProps {
  sessions: AdminSessionItem[];
  onRevokeSession: (sessionId: string) => Promise<void>;
  onRevokeAllSessions: (keepCurrent: boolean) => Promise<void>;
}

export const SessionsSection: React.FC<SessionsSectionProps> = ({
  sessions,
  onRevokeSession,
  onRevokeAllSessions,
}) => {
  const [isRevoking, setIsRevoking] = useState(false);

  const handleRevokeSingle = async (session: AdminSessionItem) => {
    if (confirm(`Revoke active session for ${session.email}?`)) {
      setIsRevoking(true);
      try {
        await onRevokeSession(session.id);
      } catch (err: unknown) {
        alert(err instanceof Error ? err.message : 'Failed to revoke session');
      } finally {
        setIsRevoking(false);
      }
    }
  };

  const handleRevokeAll = async (keepCurrent: boolean) => {
    const promptMsg = keepCurrent
      ? 'Revoke all other active sessions across all devices?'
      : 'Revoke ALL active sessions (including this browser session)? You will need to log in again.';

    if (confirm(promptMsg)) {
      setIsRevoking(true);
      try {
        await onRevokeAllSessions(keepCurrent);
      } catch (err: unknown) {
        alert(err instanceof Error ? err.message : 'Failed to revoke sessions');
      } finally {
        setIsRevoking(false);
      }
    }
  };

  const formatDate = (dateStr: string) => {
    try {
      return new Date(dateStr).toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="admin-section sessions-section">
      <div className="section-header">
        <div className="header-text">
          <h2>Active Sessions</h2>
          <p className="section-description">
            Inspect currently active authenticated sessions and enforce instant revocation.
          </p>
        </div>

        <div className="header-actions">
          <button
            type="button"
            disabled={isRevoking || sessions.length <= 1}
            className="action-btn action-btn-secondary"
            onClick={() => handleRevokeAll(true)}
          >
            Revoke Other Sessions
          </button>
          <button
            type="button"
            disabled={isRevoking || sessions.length === 0}
            className="action-btn action-btn-danger"
            onClick={() => handleRevokeAll(false)}
          >
            Revoke All Sessions
          </button>
        </div>
      </div>

      <div className="table-responsive">
        <table className="admin-data-table">
          <thead>
            <tr>
              <th>Account</th>
              <th>Role</th>
              <th>Status</th>
              <th>Last Active</th>
              <th>Expires</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sessions.length === 0 ? (
              <tr>
                <td colSpan={6} className="empty-state">
                  No active sessions found.
                </td>
              </tr>
            ) : (
              sessions.map(s => (
                <tr key={s.id} className={s.isCurrent ? 'row-current' : ''}>
                  <td className="cell-text">
                    <span className="account-email-inline">{s.email}</span>
                  </td>
                  <td>
                    <span className={`role-badge role-${s.role}`}>{s.role}</span>
                  </td>
                  <td>
                    {s.isCurrent ? (
                      <span className="status-pill status-active">Current Browser</span>
                    ) : (
                      <span className="status-pill status-subtle">Active</span>
                    )}
                  </td>
                  <td className="cell-meta">{formatDate(s.lastUsedAt)}</td>
                  <td className="cell-meta">{formatDate(s.expiresAt)}</td>
                  <td className="cell-actions">
                    <button
                      type="button"
                      disabled={isRevoking}
                      className="btn-link btn-danger"
                      onClick={() => handleRevokeSingle(s)}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};
